import React, { useState } from "react";
import { Popover } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

//Common
import { Drawer, Snackbar } from "../common/VFX-UI";

//App
import useAppBarStyles from "./styles";
import { getClaims, updateStorageProfile } from "../../utility/tokenHelper";
import {
    getUserInitials,
    getUserName,
    getOrganizationName,
    IsOrganizationUser,
    IsCauwUser,
} from "../../utility/helpers/claimHelper";
import { SnackbarState } from "../../types";
import { UserViewModel } from "../../viewModels/userViewModel";
import { UserDto } from "../../app/model/api/types/dto";
import { EMPTY_GUID } from "../../utility/constants/constants";
import userApi from "../../app/model/api/userApi";
import { apiErrorMessage } from "../../utility/lib/error";
import ProfileForm from "../Users/ProfileForm";
import { queryKeys } from "../Users/constants";
import ProfileView from "../Users/ProfileView";
import ChangePasswordContainer from "../Admin/ChangePassword/ChangePasswordContainer";

interface ProfileMenuProps {
    targetElementMenu: string | HTMLElement;
    profileOpen: boolean;
    toggleProfileMenuPopover: () => void;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({
    targetElementMenu,
    profileOpen,
    toggleProfileMenuPopover,
}) => {
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
    const appBarStyles = useAppBarStyles();
    const [showChangePasswordDrawer, setShowChangePasswordDrawer] =
        useState(false);
    const userAddEditSuccessMessage = "Profile updated";

    const [errorSnackbar, setErrorSnackbar] = useState<SnackbarState>({
        showErrorSnackbar: false,
        errorMessage: "",
    });

    const [snackBar, setSnackbar] = useState({
        showSuccessSnackbar: false,
        successMessage: "",
    });
    const navigate = useNavigate();
    const claims = getClaims();
    const onShowSuccess = (message: string) => {
        setIsProfileOpen(false);
        setShowChangePasswordDrawer(false);
        toggleProfileMenuPopover();
        setSnackbar({
            showSuccessSnackbar: true,
            successMessage: message,
        });
    };

    const onShowError = (error: string) => {
        setErrorSnackbar({
            showErrorSnackbar: true,
            errorMessage: error,
        });
    };
    const onAddSnackbarClose = () => {
        setSnackbar({
            showSuccessSnackbar: false,
            successMessage: "",
        });
    };
    const onLogout = () => {
        navigate("/logout");
    };

    const handleOpenProfile = () => {
        toggleProfileMenuPopover();
        setIsProfileOpen(true);
    };

    const toggleChangePasswordDrawer = async () => {
        await toggleProfileMenuPopover();
        setShowChangePasswordDrawer(!showChangePasswordDrawer);
    };

    const onChangePassword = () => {
        toggleChangePasswordDrawer();
    };

    const { status, mutate } = useMutation<void, Error, UserDto>({
        mutationFn: async (payload: UserDto) => {
            const response = await userApi.save(payload);
            const errorMessage = apiErrorMessage(response.errorMessages);
            if (errorMessage) {
                onShowError(errorMessage);
            } else {
                onShowSuccess(userAddEditSuccessMessage);
                updateProfile(payload);
                setIsEditProfileOpen(false);
            }
        },
    });

    const updateProfile = (profile: UserDto) => {
        claims.uid = profile.userId;
        claims.first_name = profile.firstName;
        claims.last_name = profile.lastName;
        claims.phone_number = profile.phoneNumber;
        updateStorageProfile(claims);
    };

    const handleOnSubmitHandler = async (values: UserViewModel) => {
        const user: UserDto = values.toDto();
        if (user.userId == null) user.userId = EMPTY_GUID;
        user.isDeleted = false;
        user.isActive = true;
        mutate(user);
    };

    const handleEditActionHandler = async () => {
        setIsProfileOpen(false);
        setIsEditProfileOpen(true);
    };

    const renderMenu = (
        <div className="p-2 pb-4">
            <div style={appBarStyles.profileTopContainer} className="mt-1 mb-4 mx-1">
                <span style={appBarStyles.profileText}>{getOrganizationName()}</span>
                <span style={appBarStyles.profileLink} onClick={onLogout}>
                    Sign Out
                </span>
            </div>
            <div className="d-flex justify-content-between flex-row px-2">
                <div>
                    <span
                        style={appBarStyles.profileInitials}
                        className="rounded-circle"
                    >
                        {getUserInitials()}
                    </span>
                </div>
                <div className="flex-fill mb-3" style={appBarStyles.profileInfoContainer}>
                    <div className="d-flex align-items-end flex-column">
                        <div className="mb-1" style={appBarStyles.profileName}>
                            {getUserName()}
                        </div>
                        <div className="mb-1" style={appBarStyles.profileInfo}>
                            {claims && claims.email}
                        </div>
                        {(IsOrganizationUser() || IsCauwUser()) && claims?.role && (
                            <div className="mb-1" style={appBarStyles.profileInfo}>
                                {claims.role}
                            </div>
                        )}

                        {(IsOrganizationUser() || IsCauwUser()) && (
                            <div className="d-flex align-items-end flex-column mt-2">
                                <span
                                    style={appBarStyles.profileLink}
                                    onClick={handleOpenProfile}
                                >
                                    View Account
                                </span>
                                <span
                                    style={appBarStyles.profileLink}
                                    onClick={onChangePassword}
                                >
                                    Change Password
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const { data: userLookupData } = useQuery({
        queryKey: [queryKeys.getUserLookups],
        queryFn: () => userApi.getLookups(),
    });

    const Role = userLookupData?.result?.find(
        (role) => role.name === claims?.role
    );
    const initialValues =
        claims?.user_id || claims?.sid
            ? new UserViewModel(
                claims?.user_id ?? null,
                claims?.organizationId ?? null,
                claims?.first_name ?? null,
                claims?.last_name ?? null,
                Role?.id !== null && Role?.id !== undefined ? [Role?.id] : null,
                claims?.username ?? null,
                (claims?.email && claims?.email) ?? null,
                claims?.phone_number ?? null,
                true,
                claims?.createdDateUtc ?? null
            )
            : new UserViewModel(
                EMPTY_GUID,
                EMPTY_GUID,
                null,
                null,
                null,
                null,
                null,
                null,
                true,
                null
            );

    return (
        <>
            <Popover
                placement="bottom-start"
                isOpen={profileOpen}
                trigger="legacy"
                target={targetElementMenu}
                toggle={toggleProfileMenuPopover}
                style={appBarStyles.profileContainer}
                hideArrow
            >
                {renderMenu}
            </Popover>
            <Drawer show={showChangePasswordDrawer}>
                {showChangePasswordDrawer && (
                    <ChangePasswordContainer
                        onClose={toggleChangePasswordDrawer}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>
            <Drawer show={isProfileOpen}>
                {isProfileOpen && (
                    <ProfileView
                        onClose={() => setIsProfileOpen(false)}
                        initialVal={initialValues}
                        editAction={handleEditActionHandler}
                    />
                )}
            </Drawer>
            <Drawer show={isEditProfileOpen}>
                {isEditProfileOpen && (
                    <ProfileForm
                        handleOnSubmit={handleOnSubmitHandler}
                        onClose={() => setIsEditProfileOpen(false)}
                        initialVal={initialValues}
                        status={status}
                    />
                )}
            </Drawer>
            {errorSnackbar.showErrorSnackbar && (
                <Snackbar
                    open={errorSnackbar.showErrorSnackbar}
                    message={errorSnackbar.errorMessage}
                    handleClose={onAddSnackbarClose}
                    severity="danger"
                />
            )}
            {snackBar.showSuccessSnackbar && (
                <Snackbar
                    open={snackBar.showSuccessSnackbar}
                    message={snackBar.successMessage}
                    handleClose={onAddSnackbarClose}
                    severity="success"
                />
            )}
        </>
    );
};

export default ProfileMenu;
